import { IPrintJob } from ".";

import { AssetLocation, AssetLocationTypes, Booking, KeyManagementTypes, PrinterLabelType, ShiftTask, TaskCategory } from "../../logic/api";

import { useDate } from "../../utility/useDate";

export const printableLocationTypes = [
    AssetLocationTypes.User,
    AssetLocationTypes.Lane,
    AssetLocationTypes.Zone,
    AssetLocationTypes.Hook,
    AssetLocationTypes.KeyBox,
    AssetLocationTypes.DynamicBox,
    AssetLocationTypes.Keyroom,
];

interface IPrintJobs {
    generateBarrierTokenPrintJob: (token: string) => IPrintJob;
    generateMeetAndGreetPrintJob: (booking: Booking) => IPrintJob;
    generateParkAndRidePrintJob: (booking: Booking) => IPrintJob;
    generateVehiclePrintJob: (booking: Booking) => IPrintJob;
    generateLocationPrintJob: (location: AssetLocation, parentLocation?: AssetLocation) => IPrintJob;
    generateLockerQRPrintJob: (booking: Booking, qrCode: string) => IPrintJob;
    generateUserPrintJob: (location: AssetLocation) => IPrintJob;
    generateBoxLanePrintJob: (location: AssetLocation) => IPrintJob;
    generateHookPrintJob: (location: AssetLocation) => IPrintJob;
    generateShiftTaskPrintJob: (task: ShiftTask) => IPrintJob;
}

export function useLabelTemplates(): IPrintJobs {
    const {
        toFormattedDateString,
        toFormattedDateTimeString,
        toFormattedTimeString
    } = useDate();

    const generateBarrierTokenPrintJob = (token: string): IPrintJob => {
        return {
            type: PrinterLabelType.barrierToken,
            arrayOfValues: [
                {
                    token: token
                }
            ]
        }
    }

    const generateMeetAndGreetPrintJob = (booking: Booking): IPrintJob => {
        const allocatedKeyLocker = booking.allocatedKeyLocker == null ? "NONE" : booking.allocatedKeyLocker.name;
        return {
            type: PrinterLabelType.MeetAndGreet,
            arrayOfValues: [
                {
                    ...booking,
                    ...booking.vehicle,
                    ...booking.leadContact,
                    allocatedKeyLocker,
                    barcodeWidthRatio: 3,
                    bookingId: booking.id,
                    bookingRef: booking.ref,
                    vehicleReg: booking.vehicle.registration,
                    vehicleMake: booking.vehicle.make,
                    vehicleModel: booking.vehicle.model,
                    vehicleColour: booking.vehicle.colour,
                    dueInDate: toFormattedDateTimeString(new Date(booking.bookingStartDate)),
                    arrivalDate: toFormattedDateString(new Date(booking.bookingStartDate)),
                    arrivalTime: toFormattedTimeString(new Date(booking.bookingStartDate)),
                    vehicleCollectionDate: toFormattedDateString(new Date(booking.expectedReturnDate)),
                    flightArrivalTime: toFormattedTimeString(new Date(booking.bookingStartDate)),
                    expectedReturnDate: toFormattedDateTimeString(new Date(booking.expectedReturnDate)),
                    allocatedLane: booking.vehicle.allocatedLane == null ? "NO ALLOCATION" : (`${booking.vehicle.allocatedLane.name}, ${booking.vehicle.allocatedLane.parentName}`),
                    allocatedLaneName: booking.vehicle.allocatedLane == null ? "NO ALLOCATION" : (`${booking.vehicle.allocatedLane.name}`),
                    allocatedZoneName: booking.vehicle.allocatedLane == null ? "NO ALLOCATION" : (`${booking.vehicle.allocatedLane.parentName}`),
                    allocatedLetter: booking.vehicle?.allocatedLetter,
                    allocatedTimeSlot: booking.vehicle?.allocatedTimeSlot,
                    returnTerminal: (booking.returnFlight.terminal ?? { name: "" }).name ?? "",
                    outboundTerminal: (booking.outboundFlight.terminal ?? { name: "" }).name ?? "",
                    returnDestination: (booking.returnFlight ?? { flightDestination: "" }).flightDestination ?? "",
                    outboundDestination: (booking.outboundFlight ?? { flightDestination: "" }).flightDestination ?? "",
                    laneName: booking?.vehicle?.location?.name ?? "",
                    zoneName: booking?.vehicle?.location?.parentName ?? "",
                    location: [booking?.vehicle?.location?.name ?? "", booking?.vehicle?.location?.parentName ?? ""].filter((f) => f.length > 0).join(", "),
                    arrivalLaneName: booking?.vehicle?.arrivalLane?.name ?? "",
                    arrivalZoneName: booking?.vehicle?.arrivalLane?.parentName ?? "",
                    arrivalLocation: [booking?.vehicle?.arrivalLane?.name ?? "", booking?.vehicle?.arrivalLane?.parentName ?? ""].filter((f) => f.length > 0).join(", "),
                }
            ]
        }
    }

    const generateParkAndRidePrintJob = (booking: Booking): IPrintJob => {
        const allocatedKeyLocker = booking.allocatedKeyLocker == null ? "NONE" : booking.allocatedKeyLocker.name;
        return {
            type: PrinterLabelType.parkAndRide,
            arrayOfValues: [
                {
                    ...booking,
                    ...booking.vehicle,
                    ...booking.leadContact,
                    allocatedKeyLocker,
                    barcodeWidthRatio: 3,
                    bookingId: booking.id,
                    bookingRef: booking.ref,
                    pax: booking.paxCount,
                    vehicleReg: booking.vehicle.registration,
                    vehicleMake: booking.vehicle.make,
                    vehicleModel: booking.vehicle.model,
                    vehicleColour: booking.vehicle.colour,
                    driverName: booking.leadContact.fullName,

                    arrivalLane: booking.vehicle.location == null ? "NO LOCATION" : (`${booking.vehicle.location.name}, ${booking.vehicle.location.parentName}`),
                    vehicleCollectionDate: toFormattedDateString(new Date(booking.expectedReturnDate)),
                    flightArrivalTime: toFormattedTimeString(new Date(booking.bookingStartDate)),
                    dueInDate: toFormattedDateTimeString(new Date(booking.bookingStartDate)),
                    expectedReturnDate: toFormattedDateTimeString(new Date(booking.expectedReturnDate)),
                    allocatedLane: booking.vehicle.allocatedLane == null ? "NO ALLOCATION" : (`${booking.vehicle.allocatedLane.name}, ${booking.vehicle.allocatedLane.parentName}`),
                    allocatedLaneName: booking.vehicle.allocatedLane == null ? "NO ALLOCATION" : (`${booking.vehicle.allocatedLane.name}`),
                    allocatedZoneName: booking.vehicle.allocatedLane == null ? "NO ALLOCATION" : (`${booking.vehicle.allocatedLane.parentName}`),
                    allocatedLetter: booking.vehicle?.allocatedLetter,
                    allocatedTimeSlot: booking.vehicle?.allocatedTimeSlot,
                    returnTerminal: (booking.returnFlight.terminal ?? { name: "" }).name ?? "",
                    outboundTerminal: (booking.outboundFlight.terminal ?? { name: "" }).name ?? "",
                    returnDestination: (booking.returnFlight ?? { flightDestination: "" }).flightDestination ?? "",
                    outboundDestination: (booking.outboundFlight ?? { flightDestination: "" }).flightDestination ?? "",
                    laneName: booking?.vehicle?.location?.name ?? "",
                    zoneName: booking?.vehicle?.location?.parentName ?? "",
                    location: [booking?.vehicle?.location?.name ?? "", booking?.vehicle?.location?.parentName ?? ""].filter((f) => f.length > 0).join(", "),
                    arrivalLaneName: booking?.vehicle?.arrivalLane?.name ?? "",
                    arrivalZoneName: booking?.vehicle?.arrivalLane?.parentName ?? "",
                    arrivalLocation: [booking?.vehicle?.arrivalLane?.name ?? "", booking?.vehicle?.arrivalLane?.parentName ?? ""].filter((f) => f.length > 0).join(", "),
                }
            ]
        }
    }

    const generateVehiclePrintJob = (booking: Booking): IPrintJob => {
        const allocatedKeyLocker = booking.allocatedKeyLocker == null ? "NONE" : booking.allocatedKeyLocker.name;
        return {
            type: PrinterLabelType.vehicle,
            arrayOfValues: [
                {
                    ...booking,
                    ...booking.vehicle,
                    ...booking.leadContact,
                    allocatedKeyLocker,
                    barcodeWidthRatio: 3,
                    bookingId: booking.id,
                    bookingRef: booking.ref,
                    vehicleReg: booking.vehicle.registration,
                    vehicleMake: booking.vehicle.make,
                    vehicleModel: booking.vehicle.model,
                    vehicleColour: booking.vehicle.colour,
                    dueInDate: toFormattedDateTimeString(new Date(booking.bookingStartDate)),
                    vehicleCollectionDate: toFormattedDateString(new Date(booking.expectedReturnDate)),
                    flightArrivalTime: toFormattedTimeString(new Date(booking.bookingStartDate)),
                    expectedReturnDate: toFormattedDateTimeString(new Date(booking.expectedReturnDate)),
                    allocatedLane: booking.vehicle.allocatedLane == null ? "NO ALLOCATION" : (`${booking.vehicle.allocatedLane.name}, ${booking.vehicle.allocatedLane.parentName}`),
                    allocatedLaneName: booking.vehicle.allocatedLane == null ? "NO ALLOCATION" : (`${booking.vehicle.allocatedLane.name}`),
                    allocatedZoneName: booking.vehicle.allocatedLane == null ? "NO ALLOCATION" : (`${booking.vehicle.allocatedLane.parentName}`),
                    allocatedLetter: booking.vehicle?.allocatedLetter,
                    allocatedTimeSlot: booking.vehicle?.allocatedTimeSlot,
                    returnTerminal: (booking.returnFlight.terminal ?? { name: "" }).name ?? "",
                    outboundTerminal: (booking.outboundFlight.terminal ?? { name: "" }).name ?? "",
                    returnDestination: (booking.returnFlight ?? { flightDestination: "" }).flightDestination ?? "",
                    outboundDestination: (booking.outboundFlight ?? { flightDestination: "" }).flightDestination ?? "",
                    laneName: booking?.vehicle?.location?.name ?? "",
                    zoneName: booking?.vehicle?.location?.parentName ?? "",
                    location: [booking?.vehicle?.location?.name ?? "", booking?.vehicle?.location?.parentName ?? ""].filter((f) => f.length > 0).join(", "),
                    arrivalLaneName: booking?.vehicle?.arrivalLane?.name ?? "",
                    arrivalZoneName: booking?.vehicle?.arrivalLane?.parentName ?? "",
                    arrivalLocation: [booking?.vehicle?.arrivalLane?.name ?? "", booking?.vehicle?.arrivalLane?.parentName ?? ""].filter((f) => f.length > 0).join(", "),
                }
            ]
        }
    }

    const generateLocationPrintJob = (location: AssetLocation, parentLocation?: AssetLocation): IPrintJob => {
        if (location.type == AssetLocationTypes.User) {
            return generateUserPrintJob(location);
        } else if (location.type == AssetLocationTypes.Lane) {
            if (location.keyType == KeyManagementTypes.hook) {
                return generateHookPrintJob(location);
            } else if (parentLocation != null) {
                if (parentLocation.keyType == KeyManagementTypes.box) {
                    return generateBoxLanePrintJob(location);
                } else if (parentLocation.keyType == KeyManagementTypes.hook) {
                    return generateHookPrintJob(location);
                }
            }
        }
        return generateHookPrintJob(location);
    }

    const generateLockerQRPrintJob = (booking: Booking, qrCode: string): IPrintJob => {
        return {
            type: PrinterLabelType.lockerQrCode,
            arrayOfValues: [{
                "qrCode": qrCode,
                ...booking,
                ...booking.vehicle,
                ...booking.leadContact,
            }]
        }
    }

    const generateUserPrintJob = (location: AssetLocation): IPrintJob => {
        return {
            type: PrinterLabelType.user,
            arrayOfValues: [{
                "staffPass": location.code,
                "firstName": (location.name || "").split(" ")[0],
                "lastName": (location.name || "").split(" ")[1] || ""
            }]
        }
    }

    const generateBoxLanePrintJob = (location: AssetLocation): IPrintJob => {
        return {
            type: PrinterLabelType.boxLane,
            arrayOfValues: [{
                "laneName": location.name,
                "zoneName": location.parentName,
                "code": location.code,
            }]
        }
    }

    const generateHookPrintJob = (location: AssetLocation): IPrintJob => {
        return {
            type: PrinterLabelType.keyHook,
            arrayOfValues: [{
                "laneName": location.name,
                "zoneName": location.parentName,
                "code": location.code,
            }]
        }
        /*
        return {
            id: PrinterLabelType.keyHook,
            arrayOfValues: [{
                "BFL": "[BFL]",
                "BCL": "[BCL]",
                "BLO": "[BLO]",
                "HRL": "[HRL]",
                "BCR": "[BCR]",
                "BRO": "[BRO]",
                "HRR": "[HRR]"
            }]
        }*/
    }

    const generateShiftTaskPrintJob = (task: ShiftTask): IPrintJob => {
        return {
            type: PrinterLabelType.task,
            arrayOfValues: [{
                taskKey: task.key,
                taskTitle: task.title,
                taskCategory: task.category
            }]
        }
    }

    return {
        generateBarrierTokenPrintJob,
        generateMeetAndGreetPrintJob,
        generateParkAndRidePrintJob,
        generateVehiclePrintJob,
        generateLocationPrintJob,
        generateLockerQRPrintJob,
        generateUserPrintJob,
        generateBoxLanePrintJob,
        generateHookPrintJob,
        generateShiftTaskPrintJob
    }
}
